import React, { useEffect } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  CardHeader,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";

import EditCancel from "../../../components/EditCancel";
import useMaterial from "../../../hooks/useMaterial";
import MaterialFields from "./components/MaterialFields";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);

const validationSchema = Yup.object().shape({
  materialCode: Yup.string(),
  materialName: Yup.string().required("Material Name is required"),
  categoryId: Yup.number().required("Category is required"),
  brand: Yup.string().required("Brand is required"),
  model: Yup.string(),
  uom: Yup.string().required("UOM is required"),
  minimumStock: Yup.number().required("Minimum Stock is required"),
  description: Yup.string(),
  status: Yup.string().required("Status is required"),
});

function EditMaterial() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();

  const { material, getMaterial, updateMaterial, isSubmitting } = useMaterial();

  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { reset, handleSubmit } = methods;

  useEffect(() => {
    fetchMaterial();
  }, []);

  useEffect(() => {
    if (material && Object.keys(material).length > 0) {
      reset({
        materialId: material.materialId,
        materialCode: material.materialCode,
        materialName: material.materialName,
        categoryId: material.categoryId,
        brand: material.brand,
        model: material.model,
        uom: material.uom,
        minimumStock: material.minimumStock,
        description: material.description,
        status: material.status,
      });
    }
  }, [material]);

  const fetchMaterial = async () => {
    await getMaterial(state.id);
  };

  const onSubmit = async (data) => {
    try {
      await updateMaterial(data);

      dispatch(showSuccessSnackbar("Material updated successfully!"));

      navigate("/inventory-mgnt/material/list", { state: { tab: 1 } });
    } catch (error) {
      dispatch(showErrorSnackbar(error.message));
    }
  };

  const cancelBtn = () => {
    navigate("/inventory-mgnt/material/list", { state: { tab: 1 } });
  };

  const breadCrump = () => (
    <Breadcrumbs aria-label="Breadcrumb" mt={2}>
      <Typography>Inventory</Typography>
      <Typography>Material Master</Typography>
      <Typography>Edit</Typography>
    </Breadcrumbs>
  );

  return (
    <FormProvider {...methods}>
      <Card mb={6}>
        <CardHeader title={t("edit_material")} subheader={breadCrump()} />

        <CardContent>
          {isSubmitting ? (
            <Box display="flex" justifyContent="center" my={6}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <MaterialFields fields={validationSchema.fields} />

              <br />

              <EditCancel cancelBtn={cancelBtn} />
            </form>
          )}
        </CardContent>
      </Card>
    </FormProvider>
  );
}

export default EditMaterial;
